import { useQuery } from 'react-query';
import { useAxiosClient } from '../utils/useAxiosClient';
import { PillarPageData } from './usePillarsPageData';
import { useFilterContext } from '../context/FilterContext';
import React from 'react';

/**
 * Custom hook for fetching pillar engagement data for personal branding
 * This uses the same endpoint as usePillarsPageData but is specifically
 * for the personal branding dashboard
 */
export function usePersonalBrandingPillars(
  orgId: number,
  selectedPillars: string[] = [],
  startDate: string | null = null,
  endDate: string | null = null,
  selectedBrands: string[] = [],
  defaultToFirstBrand: boolean = false
) {
  const axiosClient = useAxiosClient();
  const { availableBrands } = useFilterContext();
  
  // Determine brands to use: if empty and defaultToFirstBrand is true, use first available brand
  const brandsToUse = React.useMemo(() => {
    if (selectedBrands.length > 0) {
      return selectedBrands;
    }
    if (defaultToFirstBrand && availableBrands.length > 0) {
      return [availableBrands[0].id];
    }
    return selectedBrands; // Empty array
  }, [selectedBrands, defaultToFirstBrand, availableBrands]);
  
  // Build the query parameters
  const queryParams = new URLSearchParams();
  if (orgId) queryParams.append('org', orgId.toString());
  
  // Add pillars if selected
  if (selectedPillars && selectedPillars.length > 0) {
    selectedPillars.forEach(pillar => {
      queryParams.append('pillars', pillar);
    });
  }
  
  // Add brands if selected (using brandsToUse which may include default)
  if (brandsToUse && brandsToUse.length > 0) {
    brandsToUse.forEach(brand => {
      queryParams.append('brands', brand);
    });
  }
  
  // Add date range parameters if provided
  if (startDate) queryParams.append('start_date', startDate);
  if (endDate) queryParams.append('end_date', endDate);

  // React Query automatically caches by the key
  const {
    data,
    isLoading,
    error,
  } = useQuery(
    ['personalBrandingPillars', orgId, selectedPillars, startDate, endDate, brandsToUse],
    async () => {
      if (!orgId) {
        throw new Error('No organization selected');
      }

      const response = await axiosClient.get<PillarPageData>(
        `/api/get-pillars-page-data?${queryParams.toString()}`
      );

      // If the response has an 'error' field, throw an error
      if ('error' in response.data) {
        throw new Error(
          typeof response.data.error === 'string'
            ? response.data.error
            : 'An error occurred fetching pillar data'
        );
      }

      return response.data;
    },
    {
      enabled: !!orgId,
      staleTime: 0, // No stale time - always refresh when params change
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      retry: 1,
    }
  );

  // Return in the same format as other hooks
  return {
    data: data ?? null,
    loading: isLoading,
    error: error ? (error as Error).message : null,
  };
} 